import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './DealerInfoModal.scss';
import { ProfileEditModal } from './ProfileEditModal';
import { validateDealerInfo } from './validation';
import { useMvpPartners } from '@/hooks/useMvpPartners';
import { Loader } from '../Loader/Loader';

interface DealerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: Record<string, any>) => Promise<void>;
  initialData: {
    primaryDealer: string;
    dealerRepresentative: string;
    dealerCompanyId?: string;
    dealerRepresentativeId?: string;
    dealerCompanyRemoteId?: string;
    dealerRepresentativeRemoteId?: string;
  };
}

export const DealerInfoModal: React.FC<DealerInfoModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const { t } = useTranslation('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    primaryDealer: initialData.primaryDealer || '',
    dealerRepresentative: initialData.dealerRepresentative || '',
    dealerCompanyId: initialData.dealerCompanyId || '',
    dealerRepresentativeId: initialData.dealerRepresentativeId || '',
    dealerCompanyRemoteId: initialData.dealerCompanyRemoteId || '',
    dealerRepresentativeRemoteId: initialData.dealerRepresentativeRemoteId || ''
  });
  
  // Fetch MVP partners for dealer companies
  const { 
    data: mvpPartners = [], 
    isLoading: isLoadingDealerCompanies, 
    isError: dealerCompaniesError, 
    retailerOptions 
  } = useMvpPartners();
  
  // Extract dealer representatives from MVP partners' contacts
  const dealerRepresentatives = mvpPartners.flatMap(partner => 
    partner.contacts?.filter(contact => 
      !contact.hasLeftCompany && 
      ['Principal', 'Store Manager', 'Sales Rep'].includes(contact.dealerFunction)
    ).map(contact => ({
      ...contact,
      dealerCompanyId: partner.id,
      dealerCompanyRemoteId: partner.remoteObjectId,
      dealerCompanyName: partner.name
    })) || []
  );
  
  // Reset sales representative when dealer changes
  useEffect(() => {
    if (formData.dealerCompanyId) {
      // Find the selected company
      const selectedCompany = mvpPartners.find(
        partner => partner.remoteObjectId === formData.dealerCompanyId || 
                  partner.id.toString() === formData.dealerCompanyId
      );
      
      if (selectedCompany) {
        setFormData(prev => ({
          ...prev,
          primaryDealer: selectedCompany.name,
          dealerCompanyRemoteId: selectedCompany.remoteObjectId || ''
        }));
      }
    }
  }, [formData.dealerCompanyId, mvpPartners]);
  
  const handleDealerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      dealerCompanyId: value,
      // Reset sales representative when dealer changes
      dealerRepresentative: '',
      dealerRepresentativeId: '',
      dealerRepresentativeRemoteId: ''
    }));
  };
  
  const handleRepresentativeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const selectedRep = dealerRepresentatives.find(rep => rep.remoteObjectId === value);
    
    if (selectedRep) {
      setFormData(prev => ({
        ...prev,
        dealerRepresentativeId: value,
        dealerRepresentativeRemoteId: selectedRep.remoteObjectId || '',
        dealerRepresentative: `${selectedRep.firstname} ${selectedRep.lastname} - ${selectedRep.dealerFunction}`
      }));
    }
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSuccess(formData);
      onClose();
    } catch (error) {
      console.error('Error updating dealer info:', error);
    }
    setIsSubmitting(false);
  };

  return (
    <ProfileEditModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('sections.dealer.title').toUpperCase()}
      validate={validateDealerInfo}
      isSubmitting={isSubmitting}
    >
      {isLoadingDealerCompanies ? (
        <Loader variant="overlay" />
      ) : dealerCompaniesError ? (
        <div className="dealer-error">
          <p>{t('sections.dealer.error')}</p>
        </div>
      ) : (
        <div className="form-groups-grid">
          <div className="form-group">
            <label htmlFor="dealerCompanyId">{t('sections.dealer.primaryDealer')}</label>
            <select
              id="dealerCompanyId"
              name="dealerCompanyId"
              className="form-field__select"
              value={formData.dealerCompanyId}
              onChange={handleDealerChange}
            >
              <option value="">{t('sections.dealer.primaryDealerPlaceholder')}</option>
              {retailerOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dealerRepresentativeId">{t('sections.dealer.salesRepresentative')}</label>
            <select
              id="dealerRepresentativeId"
              name="dealerRepresentativeId"
              className="form-field__select"
              value={formData.dealerRepresentativeId}
              onChange={handleRepresentativeChange}
              disabled={!formData.dealerCompanyId}
            >
              <option value="">{t('sections.dealer.selectRepresentativePlaceHolder')}</option>
              {dealerRepresentatives
                .filter(representative => {
                  // Only show representatives from the selected dealer
                  return representative.dealerCompanyRemoteId === formData.dealerCompanyId || 
                         representative.dealerCompanyId.toString() === formData.dealerCompanyId;
                })
                .map((representative) => (
                  <option key={representative.remoteObjectId} value={representative.remoteObjectId}>
                    {`${representative.firstname} ${representative.lastname} - ${representative.dealerFunction}`}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}
    </ProfileEditModal>
  );
};
